import {
    ArrowDownward as ArrowDownIcon,
    ArrowUpward as ArrowUpIcon,
    Delete as DeleteIcon,
    Image as ImageIcon,
    VideoFile as VideoIcon,
    Gif as GifIcon,
    Link as LinkIcon,
    Visibility as PreviewIcon,
    TextFields as TextIcon,
    Title as TitleIcon
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Switch,
    TextField,
    Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import NewsRenderer from '../components/NewsRenderer';
import GiphySearchModal from '../components/GiphySearchModal';
import { useNotification } from '../contexts/NotificationContext';
import { newsService } from '../services/newsService';

// Types de blocs disponibles
const BLOCK_TYPES = {
  TITLE: 'title',
  TEXT: 'text',
  IMAGE: 'image',
  VIDEO: 'video',
  GIF: 'gif',
  BUTTON: 'button',
  DIVIDER: 'divider',
  SPACER: 'spacer',
};

const NewsEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    summary: '',
    isPublished: false,
    coverImage: null,
  });
  const [coverImagePreview, setCoverImagePreview] = useState('');
  const [blocks, setBlocks] = useState([]);
  const [error, setError] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [giphyModalOpen, setGiphyModalOpen] = useState(false);
  const [currentGifBlockId, setCurrentGifBlockId] = useState(null);

  useEffect(() => {
    if (isEdit) {
      fetchNews();
    }
  }, [id]);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const response = await newsService.getById(id);
      const newsData = response.data;

      setFormData({
        title: newsData.title || '',
        slug: newsData.slug || '',
        summary: newsData.summary || '',
        isPublished: newsData.isPublished || false,
        coverImage: null,
      });

      if (newsData.coverImage) {
        setCoverImagePreview(`${process.env.REACT_APP_API_URL || 'https://api.inklandtattoo.fr'}${newsData.coverImage}`);
      }

      setBlocks(newsData.content?.blocks || []);
    } catch (error) {
      console.error('Erreur chargement actualité:', error);
      setError('Erreur lors du chargement de l\'actualité');
      showNotification('Erreur lors du chargement', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    // Générer automatiquement le slug depuis le titre
    if (name === 'title' && !isEdit) {
      const slug = value
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      setFormData((prev) => ({ ...prev, slug }));
    }
  };

  const handleCoverImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({ ...prev, coverImage: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setCoverImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Gestion des blocs
  const addBlock = (type) => {
    const newBlock = {
      id: Date.now(),
      type,
      data: getDefaultBlockData(type),
    };
    setBlocks([...blocks, newBlock]);
  };

  const getDefaultBlockData = (type) => {
    switch (type) {
      case BLOCK_TYPES.TITLE:
        return { text: '', level: 'h2' };
      case BLOCK_TYPES.TEXT:
        return { text: '', align: 'left' };
      case BLOCK_TYPES.IMAGE:
        return { url: '', alt: '', caption: '', file: null };
      case BLOCK_TYPES.VIDEO:
        return { url: '', caption: '', file: null, thumbnail: '', autoplay: false, loop: false };
      case BLOCK_TYPES.GIF:
        return { url: '', caption: '', file: null };
      case BLOCK_TYPES.BUTTON:
        return { text: 'Cliquez ici', url: '', color: 'primary', variant: 'contained' };
      case BLOCK_TYPES.DIVIDER:
        return { style: 'solid' };
      case BLOCK_TYPES.SPACER:
        return { height: 20 };
      default:
        return {};
    }
  };

  const updateBlock = (id, data) => {
    setBlocks(blocks.map((block) => (block.id === id ? { ...block, data } : block)));
  };

  const deleteBlock = (id) => {
    setBlocks(blocks.filter((block) => block.id !== id));
  };

  const moveBlock = (id, direction) => {
    const index = blocks.findIndex((block) => block.id === id);
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === blocks.length - 1)
    ) {
      return;
    }

    const newBlocks = [...blocks];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newBlocks[index], newBlocks[targetIndex]] = [newBlocks[targetIndex], newBlocks[index]];
    setBlocks(newBlocks);
  };

  const handleImageUpload = async (blockId, file) => {
    try {
      const response = await newsService.uploadContentImage(file);
      const imageUrl = response.data.url;

      updateBlock(blockId, {
        ...blocks.find((b) => b.id === blockId).data,
        url: imageUrl,
      });

      showNotification('Image uploadée avec succès', 'success');
    } catch (error) {
      console.error('Erreur upload image:', error);
      showNotification('Erreur lors de l\'upload de l\'image', 'error');
    }
  };

  const handleVideoUpload = async (blockId, file) => {
    try {
      const response = await newsService.uploadMedia(file, 'video');
      const videoUrl = response.data.media.url;

      updateBlock(blockId, {
        ...blocks.find((b) => b.id === blockId).data,
        url: videoUrl,
      });

      showNotification('Vidéo uploadée avec succès', 'success');
    } catch (error) {
      console.error('Erreur upload vidéo:', error);
      showNotification('Erreur lors de l\'upload de la vidéo', 'error');
    }
  };

  const handleGifUpload = async (blockId, file) => {
    try {
      const response = await newsService.uploadMedia(file, 'gif');
      const gifUrl = response.data.media.url;

      updateBlock(blockId, {
        ...blocks.find((b) => b.id === blockId).data,
        url: gifUrl,
      });

      showNotification('GIF uploadé avec succès', 'success');
    } catch (error) {
      console.error('Erreur upload GIF:', error);
      showNotification('Erreur lors de l\'upload du GIF', 'error');
    }
  };

  const handleThumbnailUpload = async (blockId, file) => {
    try {
      const response = await newsService.uploadContentImage(file);
      const thumbnailUrl = response.data.url;

      updateBlock(blockId, {
        ...blocks.find((b) => b.id === blockId).data,
        thumbnail: thumbnailUrl,
      });

      showNotification('Miniature uploadée avec succès', 'success');
    } catch (error) {
      console.error('Erreur upload miniature:', error);
      showNotification('Erreur lors de l\'upload de la miniature', 'error');
    }
  };

  const handleGiphySelect = (gif) => {
    if (currentGifBlockId) {
      updateBlock(currentGifBlockId, {
        ...blocks.find((b) => b.id === currentGifBlockId).data,
        url: gif.url,
        caption: gif.title,
        isGiphy: true,
        giphyId: gif.id,
      });
      showNotification('GIF Giphy ajouté avec succès', 'success');
    }
    setGiphyModalOpen(false);
    setCurrentGifBlockId(null);
  };

  const openGiphySearch = (blockId) => {
    setCurrentGifBlockId(blockId);
    setGiphyModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.slug) {
      showNotification('Le titre et le slug sont obligatoires', 'error');
      return;
    }

    try {
      setSaving(true);
      const newsData = {
        ...formData,
        content: { blocks },
      };

      if (isEdit) {
        await newsService.update(id, newsData);
        showNotification('Actualité mise à jour avec succès', 'success');
      } else {
        await newsService.create(newsData);
        showNotification('Actualité créée avec succès', 'success');
      }

      navigate('/news');
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      showNotification(
        `Erreur lors de ${isEdit ? 'la mise à jour' : 'la création'} de l'actualité`,
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        {isEdit ? 'Modifier l\'actualité' : 'Nouvelle actualité'}
      </Typography>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Colonne gauche - Paramètres de base */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Paramètres
              </Typography>

              <TextField
                fullWidth
                label="Titre"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                margin="normal"
              />

              <TextField
                fullWidth
                label="Slug (URL)"
                name="slug"
                value={formData.slug}
                onChange={handleInputChange}
                required
                margin="normal"
                helperText="URL de l'actualité (sans espaces ni caractères spéciaux)"
              />

              <TextField
                fullWidth
                label="Résumé"
                name="summary"
                value={formData.summary}
                onChange={handleInputChange}
                multiline
                rows={3}
                margin="normal"
                helperText="Court résumé affiché dans la liste"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isPublished}
                    onChange={handleInputChange}
                    name="isPublished"
                  />
                }
                label="Publier l'actualité"
                sx={{ mt: 2 }}
              />

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" gutterBottom>
                Image de couverture
              </Typography>

              <Button
                variant="outlined"
                component="label"
                fullWidth
                startIcon={<ImageIcon />}
                sx={{ mb: 2 }}
              >
                {formData.coverImage || coverImagePreview ? 'Changer l\'image' : 'Ajouter une image'}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleCoverImageChange}
                />
              </Button>

              {coverImagePreview && (
                <Box
                  component="img"
                  src={coverImagePreview}
                  alt="Aperçu"
                  sx={{
                    width: '100%',
                    borderRadius: 1,
                    mb: 2,
                  }}
                />
              )}
            </Paper>

            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Ajouter un bloc
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<TitleIcon />}
                  onClick={() => addBlock(BLOCK_TYPES.TITLE)}
                  fullWidth
                >
                  Titre
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<TextIcon />}
                  onClick={() => addBlock(BLOCK_TYPES.TEXT)}
                  fullWidth
                >
                  Texte
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ImageIcon />}
                  onClick={() => addBlock(BLOCK_TYPES.IMAGE)}
                  fullWidth
                >
                  Image
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<VideoIcon />}
                  onClick={() => addBlock(BLOCK_TYPES.VIDEO)}
                  fullWidth
                >
                  Vidéo
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<GifIcon />}
                  onClick={() => addBlock(BLOCK_TYPES.GIF)}
                  fullWidth
                >
                  GIF
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<LinkIcon />}
                  onClick={() => addBlock(BLOCK_TYPES.BUTTON)}
                  fullWidth
                >
                  Bouton
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => addBlock(BLOCK_TYPES.DIVIDER)}
                  fullWidth
                >
                  Séparateur
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => addBlock(BLOCK_TYPES.SPACER)}
                  fullWidth
                >
                  Espace
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* Colonne droite - Éditeur de contenu */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3, minHeight: 400 }}>
              <Typography variant="h6" gutterBottom>
                Contenu de l'actualité
              </Typography>

              {blocks.length === 0 ? (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Commencez par ajouter des blocs de contenu en utilisant les boutons de gauche
                </Alert>
              ) : (
                <Box sx={{ mt: 2 }}>
                  {blocks.map((block, index) => (
                    <BlockEditor
                      key={block.id}
                      block={block}
                      index={index}
                      totalBlocks={blocks.length}
                      onUpdate={(data) => updateBlock(block.id, data)}
                      onDelete={() => deleteBlock(block.id)}
                      onMove={(direction) => moveBlock(block.id, direction)}
                      onImageUpload={(file) => handleImageUpload(block.id, file)}
                      onVideoUpload={(file) => handleVideoUpload(block.id, file)}
                      onGifUpload={(file) => handleGifUpload(block.id, file)}
                      onThumbnailUpload={(file) => handleThumbnailUpload(block.id, file)}
                      onGiphySearch={() => openGiphySearch(block.id)}
                    />
                  ))}
                </Box>
              )}
            </Paper>

            {/* Boutons d'action */}
            <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button variant="outlined" onClick={() => navigate('/news')}>
                Annuler
              </Button>
              <Button
                variant="outlined"
                startIcon={<PreviewIcon />}
                onClick={() => setPreviewOpen(true)}
              >
                Prévisualiser
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={saving}
                startIcon={saving && <CircularProgress size={20} />}
              >
                {saving ? 'Enregistrement...' : isEdit ? 'Mettre à jour' : 'Créer'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>

      {/* Dialog de prévisualisation */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Prévisualisation</Typography>
            <Chip
              label={formData.isPublished ? 'Publié' : 'Brouillon'}
              color={formData.isPublished ? 'success' : 'default'}
              size="small"
            />
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {/* Image de couverture */}
          {coverImagePreview && (
            <Box
              component="img"
              src={coverImagePreview}
              alt={formData.title}
              sx={{
                width: '100%',
                maxHeight: 300,
                objectFit: 'cover',
                borderRadius: 1,
                mb: 3,
              }}
            />
          )}

          {/* Titre */}
          <Typography variant="h4" gutterBottom fontWeight="bold">
            {formData.title || 'Sans titre'}
          </Typography>

          {/* Résumé */}
          {formData.summary && (
            <Typography
              variant="subtitle1"
              color="text.secondary"
              sx={{ mb: 3, fontStyle: 'italic' }}
            >
              {formData.summary}
            </Typography>
          )}

          {/* Contenu avec les blocs */}
          <NewsRenderer content={{ blocks }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Modal de recherche Giphy */}
      <GiphySearchModal
        open={giphyModalOpen}
        onClose={() => {
          setGiphyModalOpen(false);
          setCurrentGifBlockId(null);
        }}
        onSelect={handleGiphySelect}
      />
    </Box>
  );
};

// Composant pour éditer un bloc
const BlockEditor = ({ block, index, totalBlocks, onUpdate, onDelete, onMove, onImageUpload, onVideoUpload, onGifUpload, onGiphySearch, onThumbnailUpload }) => {
  const renderBlockEditor = () => {
    switch (block.type) {
      case BLOCK_TYPES.TITLE:
        return (
          <Box>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Niveau</InputLabel>
              <Select
                value={block.data.level || 'h2'}
                label="Niveau"
                onChange={(e) => onUpdate({ ...block.data, level: e.target.value })}
              >
                <MenuItem value="h1">Titre principal (H1)</MenuItem>
                <MenuItem value="h2">Titre secondaire (H2)</MenuItem>
                <MenuItem value="h3">Titre tertiaire (H3)</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Texte du titre"
              value={block.data.text || ''}
              onChange={(e) => onUpdate({ ...block.data, text: e.target.value })}
              multiline
            />
          </Box>
        );

      case BLOCK_TYPES.TEXT:
        return (
          <Box>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Alignement</InputLabel>
              <Select
                value={block.data.align || 'left'}
                label="Alignement"
                onChange={(e) => onUpdate({ ...block.data, align: e.target.value })}
              >
                <MenuItem value="left">Gauche</MenuItem>
                <MenuItem value="center">Centré</MenuItem>
                <MenuItem value="right">Droite</MenuItem>
                <MenuItem value="justify">Justifié</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Texte"
              value={block.data.text || ''}
              onChange={(e) => onUpdate({ ...block.data, text: e.target.value })}
              multiline
              rows={4}
            />
          </Box>
        );

      case BLOCK_TYPES.IMAGE:
        return (
          <Box>
            <Button
              variant="outlined"
              component="label"
              fullWidth
              startIcon={<ImageIcon />}
              sx={{ mb: 2 }}
            >
              {block.data.url ? 'Changer l\'image' : 'Uploader une image'}
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    onImageUpload(file);
                  }
                }}
              />
            </Button>

            {block.data.url && (
              <Box
                component="img"
                src={block.data.url.startsWith('http') 
                  ? block.data.url 
                  : `${process.env.REACT_APP_API_URL || 'https://api.inklandtattoo.fr'}${block.data.url}`}
                alt={block.data.alt || 'Image'}
                sx={{
                  width: '100%',
                  maxHeight: 300,
                  objectFit: 'contain',
                  borderRadius: 1,
                  mb: 2,
                }}
              />
            )}

            <TextField
              fullWidth
              label="Texte alternatif"
              value={block.data.alt || ''}
              onChange={(e) => onUpdate({ ...block.data, alt: e.target.value })}
              size="small"
              sx={{ mb: 1 }}
            />
            <TextField
              fullWidth
              label="Légende (optionnel)"
              value={block.data.caption || ''}
              onChange={(e) => onUpdate({ ...block.data, caption: e.target.value })}
              size="small"
            />
          </Box>
        );

      case BLOCK_TYPES.VIDEO:
        return (
          <Box>
            <Button
              variant="outlined"
              component="label"
              fullWidth
              startIcon={<VideoIcon />}
              sx={{ mb: 2 }}
            >
              {block.data.url ? 'Changer la vidéo' : 'Uploader une vidéo'}
              <input
                type="file"
                hidden
                accept="video/*"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    onVideoUpload(file);
                  }
                }}
              />
            </Button>

            {block.data.url && (
              <Box sx={{ mb: 2 }}>
                <video
                  src={block.data.url.startsWith('http') 
                    ? block.data.url 
                    : `${process.env.REACT_APP_API_URL || 'https://api.inklandtattoo.fr'}${block.data.url}`}
                  controls
                  style={{
                    width: '100%',
                    maxHeight: 300,
                    borderRadius: 4,
                  }}
                />
              </Box>
            )}

            <Button
              variant="outlined"
              component="label"
              fullWidth
              startIcon={<ImageIcon />}
              sx={{ mb: 2 }}
            >
              {block.data.thumbnail ? 'Changer la miniature' : 'Uploader une miniature'}
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    onThumbnailUpload(file);
                  }
                }}
              />
            </Button>

            {block.data.thumbnail && (
              <Box sx={{ mb: 2, position: 'relative' }}>
                <img
                  src={block.data.thumbnail.startsWith('http')
                    ? block.data.thumbnail
                    : `${process.env.REACT_APP_API_URL || 'https://api.inklandtattoo.fr'}${block.data.thumbnail}`}
                  alt="Miniature"
                  style={{
                    width: '100%',
                    maxHeight: 200,
                    objectFit: 'cover',
                    borderRadius: 4,
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: 24,
                  }}
                >
                  ▶
                </Box>
              </Box>
            )}

            <TextField
              fullWidth
              label="Légende"
              value={block.data.caption || ''}
              onChange={(e) => onUpdate({ ...block.data, caption: e.target.value })}
              size="small"
              sx={{ mb: 2 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={block.data.autoplay || false}
                  onChange={(e) => onUpdate({ ...block.data, autoplay: e.target.checked })}
                />
              }
              label="Lecture automatique"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={block.data.loop || false}
                  onChange={(e) => onUpdate({ ...block.data, loop: e.target.checked })}
                />
              }
              label="Lecture en boucle"
            />
          </Box>
        );

      case BLOCK_TYPES.GIF:
        return (
          <Box>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  startIcon={<GifIcon />}
                >
                  {block.data.url ? 'Changer le GIF' : 'Uploader un GIF'}
                  <input
                    type="file"
                    hidden
                    accept=".gif,.webp"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        onGifUpload(file);
                      }
                    }}
                  />
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={onGiphySearch}
                  sx={{
                    background: 'linear-gradient(45deg, #00CCFF 30%, #9933FF 90%)',
                    color: 'white',
                  }}
                >
                  🎬 Rechercher sur GIPHY
                </Button>
              </Grid>
            </Grid>

            {block.data.url && (
              <Box
                component="img"
                src={block.data.url.startsWith('http') 
                  ? block.data.url 
                  : `${process.env.REACT_APP_API_URL || 'https://api.inklandtattoo.fr'}${block.data.url}`}
                alt={block.data.caption || 'GIF'}
                sx={{
                  width: '100%',
                  maxHeight: 300,
                  objectFit: 'contain',
                  borderRadius: 1,
                  mb: 2,
                }}
              />
            )}

            <TextField
              fullWidth
              label="Légende"
              value={block.data.caption || ''}
              onChange={(e) => onUpdate({ ...block.data, caption: e.target.value })}
              size="small"
            />
          </Box>
        );

      case BLOCK_TYPES.BUTTON:
        return (
          <Box>
            <TextField
              fullWidth
              label="Texte du bouton"
              value={block.data.text || ''}
              onChange={(e) => onUpdate({ ...block.data, text: e.target.value })}
              size="small"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="URL de destination"
              value={block.data.url || ''}
              onChange={(e) => onUpdate({ ...block.data, url: e.target.value })}
              size="small"
              sx={{ mb: 2 }}
              helperText="URL complète (ex: https://example.com ou /products/123)"
            />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Couleur</InputLabel>
                  <Select
                    value={block.data.color || 'primary'}
                    label="Couleur"
                    onChange={(e) => onUpdate({ ...block.data, color: e.target.value })}
                  >
                    <MenuItem value="primary">Primaire</MenuItem>
                    <MenuItem value="secondary">Secondaire</MenuItem>
                    <MenuItem value="success">Succès</MenuItem>
                    <MenuItem value="error">Erreur</MenuItem>
                    <MenuItem value="warning">Attention</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Variante</InputLabel>
                  <Select
                    value={block.data.variant || 'contained'}
                    label="Variante"
                    onChange={(e) => onUpdate({ ...block.data, variant: e.target.value })}
                  >
                    <MenuItem value="contained">Plein</MenuItem>
                    <MenuItem value="outlined">Contour</MenuItem>
                    <MenuItem value="text">Texte</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        );

      case BLOCK_TYPES.DIVIDER:
        return (
          <FormControl fullWidth size="small">
            <InputLabel>Style</InputLabel>
            <Select
              value={block.data.style || 'solid'}
              label="Style"
              onChange={(e) => onUpdate({ ...block.data, style: e.target.value })}
            >
              <MenuItem value="solid">Ligne pleine</MenuItem>
              <MenuItem value="dashed">Tirets</MenuItem>
              <MenuItem value="dotted">Points</MenuItem>
            </Select>
          </FormControl>
        );

      case BLOCK_TYPES.SPACER:
        return (
          <TextField
            fullWidth
            type="number"
            label="Hauteur (pixels)"
            value={block.data.height || 20}
            onChange={(e) =>
              onUpdate({ ...block.data, height: parseInt(e.target.value) || 20 })
            }
            size="small"
          />
        );

      default:
        return null;
    }
  };

  const getBlockTypeLabel = (type) => {
    switch (type) {
      case BLOCK_TYPES.TITLE:
        return 'Titre';
      case BLOCK_TYPES.TEXT:
        return 'Texte';
      case BLOCK_TYPES.IMAGE:
        return 'Image';
      case BLOCK_TYPES.VIDEO:
        return 'Vidéo';
      case BLOCK_TYPES.GIF:
        return 'GIF';
      case BLOCK_TYPES.BUTTON:
        return 'Bouton';
      case BLOCK_TYPES.DIVIDER:
        return 'Séparateur';
      case BLOCK_TYPES.SPACER:
        return 'Espace';
      default:
        return 'Bloc';
    }
  };

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Chip label={getBlockTypeLabel(block.type)} size="small" color="primary" />
          <Box>
            <IconButton
              size="small"
              onClick={() => onMove('up')}
              disabled={index === 0}
            >
              <ArrowUpIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => onMove('down')}
              disabled={index === totalBlocks - 1}
            >
              <ArrowDownIcon />
            </IconButton>
            <IconButton size="small" onClick={onDelete} color="error">
              <DeleteIcon />
            </IconButton>
          </Box>
        </Box>
        {renderBlockEditor()}
      </CardContent>
    </Card>
  );
};

export default NewsEdit;

